import React, {createContext, useContext, useEffect, useMemo, useState} from "react";
import {
    Layout,
    Breadcrumb,
    Row,
    Col,
    Table,
    Tag,
    Button,
    Image,
    Select,
    Space,
    message,
} from 'antd';
import Api from "../api.js";
import {Link} from "react-router-dom";
import CreateCompetition from "../components/create-competition";
import {EditOutlined, MenuOutlined} from "@ant-design/icons";
import dayjs from 'dayjs';
import {DndContext, PointerSensor, useSensor, useSensors} from '@dnd-kit/core';
import {restrictToVerticalAxis} from '@dnd-kit/modifiers';
import {
    arrayMove,
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';

const {Content} = Layout;

const RowContext = createContext({});

const DragHandle = () => {
    const {setActivatorNodeRef, listeners} = useContext(RowContext);
    return (
        <MenuOutlined
            ref={setActivatorNodeRef}
            style={{cursor: 'grab', color: '#999'}}
            {...listeners}
        />
    );
};

const TableRow = (props) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        setActivatorNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({
        id: props['data-row-key'],
    });

    const contextValue = useMemo(
        () => ({setActivatorNodeRef, listeners}),
        [setActivatorNodeRef, listeners],
    );

    const style = {
        ...props.style,
        transform: CSS.Translate.toString(transform),
        transition,
        ...(isDragging ? {position: 'relative', zIndex: 9999} : {}),
    };

    return (
        <RowContext.Provider value={contextValue}>
            <tr {...props} ref={setNodeRef} style={style} {...attributes} />
        </RowContext.Provider>
    );
};

const TYPE_OPTIONS = [
    {value: '', label: 'All types'},
    {value: 'steps', label: 'Steps'},
    {value: 'bingo', label: 'Bingo'},
];

const sortCompetitions = (items) =>
    [...items].sort((a, b) => {
        const orderA = a.sort_order ?? Number.MAX_SAFE_INTEGER;
        const orderB = b.sort_order ?? Number.MAX_SAFE_INTEGER;
        if (orderA !== orderB) {
            return orderA - orderB;
        }
        return dayjs(b.created_at).valueOf() - dayjs(a.created_at).valueOf();
    });

const Competitions = () => {
    const [competitions, setCompetitions] = useState([]);
    const [competitionData, setCompetitionData] = useState('');
    const [loading, setLoading] = useState(false);
    const [savingOrder, setSavingOrder] = useState(false);
    const [typeFilter, setTypeFilter] = useState('');

    useEffect(() => {
        window.scrollTo(0, 0);
        getCompetitions();
    }, []);

    const getCompetitions = () => {
        setLoading(true);

        Api.get('/admin/v2/competitions')
            .then(res => {
                setCompetitions(sortCompetitions(res.data));
            })
            .finally(() => setLoading(false));
    };

    const displayedCompetitions = typeFilter
        ? competitions.filter(comp => comp.type === typeFilter)
        : competitions;

    const canReorder = !typeFilter;

    const sensors = useSensors(
        useSensor(PointerSensor, {activationConstraint: {distance: 1}}),
    );

    const saveOrder = (reordered) => {
        const order = reordered.map((comp, index) => ({
            id: comp.id,
            sort_order: index + 1,
        }));

        setSavingOrder(true);

        Api.put('/admin/v2/competitions/order', {order})
            .then(() => {
                message.success('Competition order updated');
            })
            .catch(() => {
                message.error('Failed to update order');
                getCompetitions();
            })
            .finally(() => setSavingOrder(false));
    };

    const onDragEnd = ({active, over}) => {
        if (!canReorder || !over || active.id === over.id) {
            return;
        }

        setCompetitions(prev => {
            const activeIndex = prev.findIndex(c => c.id === active.id);
            const overIndex = prev.findIndex(c => c.id === over.id);
            const reordered = arrayMove(prev, activeIndex, overIndex);
            saveOrder(reordered);
            return reordered;
        });
    };

    const columns = [
        ...(canReorder ? [{
            title: '',
            key: 'sort',
            width: 40,
            render: () => <DragHandle />,
        }] : []),
        {
            title: '#',
            dataIndex: 'sort_order',
            key: 'sort_order',
            width: 50,
            render: (value, record, index) => value ?? index + 1,
        },
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 60,
        },
        {
            title: 'image',
            dataIndex: 'image',
            key: 'image',
            width: 100,
            render: (text) => <Image style={{maxWidth: "100px"}} src={text} alt=""/>,
        },
        {
            title: 'name',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => <Link to={"/competitions/" + record.id}>{text}</Link>,
        },
        {
            title: 'Participants',
            dataIndex: 'participants_count',
            key: 'participants_count',
            width: 100,
        },
        {
            title: 'begin_date',
            dataIndex: 'begin_date',
            key: 'begin_date',
            width: 140,
            render: (text) => <span>{dayjs(text).format('YYYY-MM-DD')}</span>,
        },
        {
            title: 'end_date',
            dataIndex: 'end_date',
            key: 'end_date',
            width: 140,
            render: (text) => <span>{dayjs(text).format('YYYY-MM-DD')}</span>,
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            width: 120,
            render: (type, record) => record.type === 'bingo' ? (
                <Link to={"/bingos/" + record.bingo_id}>{type} #{record.bingo_id}</Link>
            ) : type,
        },
        {
            title: 'Is paid',
            dataIndex: 'is_paid',
            key: 'is_paid',
            render: (isPaid) => (
                isPaid ? <Tag color="gold">Paid</Tag> : <Tag color="green">Free</Tag>
            ),
            width: 80,
        },
        {
            title: 'Status',
            dataIndex: 'enabled',
            key: 'enabled',
            width: 100,
            render: text => (
                <Tag color={text ? "green" : "red"}>{text ? "active" : "inactive"}</Tag>
            ),
        },
        {
            title: 'action',
            dataIndex: 'action',
            key: 'action',
            width: 100,
            render: (_, record) => (
                <Button onClick={() => setCompetitionData(record)}>
                    <EditOutlined/>
                </Button>
            ),
        },
    ];

    const table = (
        <Table
            size="small"
            dataSource={displayedCompetitions}
            columns={columns}
            rowKey="id"
            bordered
            pagination={false}
            loading={loading || savingOrder}
            components={canReorder ? {body: {row: TableRow}} : undefined}
        />
    );

    return (
        <Content style={{margin: '0 16px'}}>
            <Breadcrumb style={{margin: '16px 0'}}>
                <Breadcrumb.Item>Fitlap</Breadcrumb.Item>
                <Breadcrumb.Item>Services</Breadcrumb.Item>
                <Breadcrumb.Item>Competitions</Breadcrumb.Item>
            </Breadcrumb>

            <Col span={24}>
                <div className="site-layout-card">
                    <Row type="flex" justify="space-between" align="middle" style={{marginBottom: 20}}>
                        <h1 style={{margin: 0}}>Competitions</h1>

                        <CreateCompetition
                            competitionData={competitionData}
                            setCompetitionData={setCompetitionData}
                            callback={getCompetitions}
                        />
                    </Row>

                    <Space style={{marginBottom: 16}} wrap>
                        <Select
                            value={typeFilter}
                            onChange={setTypeFilter}
                            options={TYPE_OPTIONS}
                            style={{width: 160}}
                        />
                        {typeFilter && (
                            <span style={{color: '#999', fontSize: 13}}>
                                Clear type filter to reorder competitions
                            </span>
                        )}
                    </Space>

                    {canReorder ? (
                        <DndContext
                            sensors={sensors}
                            modifiers={[restrictToVerticalAxis]}
                            onDragEnd={onDragEnd}
                        >
                            <SortableContext
                                items={displayedCompetitions.map(c => c.id)}
                                strategy={verticalListSortingStrategy}
                            >
                                {table}
                            </SortableContext>
                        </DndContext>
                    ) : (
                        table
                    )}
                </div>
            </Col>
        </Content>
    );
};

export default Competitions;
